import axios from 'axios';
import { API_BASE } from './apiBase';

const api = axios.create({ baseURL: API_BASE });

export { API_BASE };

// Calls that are part of signing in. They must never carry the session token, and a 401
// from them means "bad credentials" / "enrollment token expired" — not a dead session.
const AUTH_FLOW_PATHS = ['/auth/login', '/auth/mfa/setup', '/auth/mfa/verify'];

// One-shot notice handed to the login form when a dead session forced a redirect, so the
// user sees why they are back at the credentials form instead of a silent bounce.
const AUTH_NOTICE_KEY = 'auth_notice';

// A request interceptor sees the relative url, but the error handler can be given an
// absolute one (axios resolves baseURL before the adapter runs, and that resolved config
// is what rides along on some failures). Compare pathnames so both forms match and a
// query string cannot defeat the check.
const pathOf = (url) => {
  if (!url) return '';
  try {
    return new URL(url, API_BASE).pathname;
  } catch {
    return String(url).split('?')[0];
  }
};

const matchesPath = (url, wanted) => {
  const path = pathOf(url);
  return path === wanted || path.endsWith(wanted);
};

const isAuthFlowPath = (url) => AUTH_FLOW_PATHS.some(p => matchesPath(url, p));

const isLoginPath = (url) => matchesPath(url, '/auth/login');

const hasAuthorization = (headers) => {
  if (!headers) return false;
  if (typeof headers.get === 'function') return !!headers.get('Authorization');
  return !!(headers.Authorization || headers.authorization);
};

export const takeAuthNotice = () => {
  try {
    const notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
    if (notice) sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return notice || null;
  } catch {
    return null;
  }
};

const dropAuthorization = (headers) => {
  if (!headers) return;
  if (typeof headers.delete === 'function') { headers.delete('Authorization'); return; }
  delete headers.Authorization;
  delete headers.authorization;
};

api.interceptors.request.use(config => {
  // /auth/login must leave with no Authorization header at all. Nothing here sets one
  // on it, but axios merges instance/global defaults into config.headers before the
  // interceptors run, so a header set anywhere else would ride along and change what
  // the server does with the credentials. Strip it rather than trust that.
  if (isLoginPath(config.url)) {
    dropAuthorization(config.headers);
    return config;
  }
  // Never overwrite an Authorization header the caller set explicitly: the MFA enrollment
  // calls carry a short-lived enrollment token, and a stale access_token left in
  // localStorage must not replace it.
  if (hasAuthorization(config.headers)) return config;
  // No sign-in call may carry a session token.
  if (isAuthFlowPath(config.url)) return config;
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !isAuthFlowPath(err.config?.url)) {
      localStorage.clear();
      // Only ever hard-navigate away from a real page. Doing it while the user is on
      // /login reloads the SPA mid-sign-in, which wipes the MFA enrollment step and every
      // toast with it — the silent "Sign In does nothing" failure.
      if (!window.location.pathname.endsWith('/login')) {
        try { sessionStorage.setItem(AUTH_NOTICE_KEY, 'Your session expired. Please sign in again.'); } catch {}
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  // mfa_code is omitted entirely on the first leg rather than sent as undefined/null, so
  // the body on the wire is exactly {email, password} — the shape the API is verified
  // against.
  login: (email, password, mfa_code) =>
    api.post('/auth/login', mfa_code ? { email, password, mfa_code } : { email, password }),
  me: () => api.get('/auth/me'),
  logout: (refresh_token) => api.post('/auth/logout', { refresh_token }),
  // MFA enrollment. The enrollment token is scoped to these two calls only — it is not a
  // session and must never be written to localStorage (/auth/me with it returns 401).
  // An empty JSON object rather than null: the endpoint takes no body, and FastAPI ignores
  // an unexpected one, but a bare null body is rejected where a body model is declared.
  mfaSetup: (enrollmentToken) =>
    api.post('/auth/mfa/setup', {}, { headers: { Authorization: `Bearer ${enrollmentToken}` } }),
  mfaVerify: (enrollmentToken, code) =>
    api.post('/auth/mfa/verify', { code }, { headers: { Authorization: `Bearer ${enrollmentToken}` } }),
};

export const tenantsAPI = {
  list: () => api.get('/tenants'),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.patch(`/tenants/${id}`, data),
  get: (id) => api.get(`/tenants/${id}`),
};

export const storesAPI = {
  list: (tenantId, params) => api.get(`/tenants/${tenantId}/stores`, { params }),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/stores`, data),
  update: (tenantId, storeId, data) => api.patch(`/tenants/${tenantId}/stores/${storeId}`, data),
  get: (tenantId, storeId) => api.get(`/tenants/${tenantId}/stores/${storeId}`),
  regeneratePairingCode: (tenantId, storeId) => api.post(`/tenants/${tenantId}/stores/${storeId}/regenerate-pairing-code`),
};

export const regionsAPI = {
  list: (tenantId) => api.get(`/tenants/${tenantId}/regions`),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/regions`, data),
};

export const devicesAPI = {
  listByStore: (storeId) => api.get(`/devices/store/${storeId}`),
  get: (id) => api.get(`/devices/${id}`),
  update: (id, data) => api.patch(`/devices/${id}`, data),
  decommission: (id) => api.post(`/devices/${id}/decommission`),
  heartbeats: (id) => api.get(`/devices/${id}/heartbeats`),
};

export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  get: (id) => api.get(`/users/${id}`),
  delete: (id) => api.delete(`/users/${id}`),
};

export const rolesAPI = {
  list: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
};

export const permissionsAPI = {
  list: () => api.get('/permissions'),
};

export const schedulesAPI = {
  list: (params) => api.get('/schedules', { params }),
  create: (data) => api.post('/schedules', data),
  update: (id, data) => api.patch(`/schedules/${id}`, data),
  delete: (id) => api.delete(`/schedules/${id}`),
};

export const volumeAPI = {
  list: (params) => api.get('/volume-rules', { params }),
  create: (data) => api.post('/volume-rules', data),
  update: (id, data) => api.patch(`/volume-rules/${id}`, data),
  approve: (id) => api.post(`/volume-rules/${id}/approve`),
};

export const campaignsAPI = {
  list: (params) => api.get('/campaigns', { params }),
  create: (data) => api.post('/campaigns', data),
  get: (id) => api.get(`/campaigns/${id}`),
  update: (id, data) => api.patch(`/campaigns/${id}`, data),
  submit: (id) => api.post(`/campaigns/${id}/submit`),
  review: (id, data) => api.post(`/campaigns/${id}/review`, data),
  launch: (id) => api.post(`/campaigns/${id}/launch`),
  pause: (id) => api.post(`/campaigns/${id}/pause`),
  summary: (id) => api.get(`/reporting/campaigns/${id}/summary`),
};

export const advertisersAPI = {
  list: (params) => api.get('/advertisers', { params }),
  create: (data) => api.post('/advertisers', data),
  update: (id, data) => api.patch(`/advertisers/${id}`, data),
};

export const aiAPI = {
  listContent: (tenantId, params) => api.get(`/ai/tenants/${tenantId}/content`, { params }),
  generate: (tenantId, data) => api.post(`/ai/tenants/${tenantId}/generate`, data),
  review: (itemId, data) => api.post(`/ai/content/${itemId}/review`, data),
  activate: (itemId) => api.post(`/ai/content/${itemId}/activate`),
  listLanguages: () => api.get('/ai/languages'),
  listVoices: (params) => api.get('/ai/voices', { params }),
};

export const djAPI = {
  getProfile: (tenantId) => api.get(`/ai/dj/tenants/${tenantId}/profile`),
  saveProfile: (tenantId, data) => api.post(`/ai/dj/tenants/${tenantId}/profile`, data),
  addSuggestion: (tenantId, suggestion) => api.post(`/ai/dj/tenants/${tenantId}/suggestions`, null, { params: { suggestion } }),
  removeSuggestion: (tenantId, suggestion) => api.delete(`/ai/dj/tenants/${tenantId}/suggestions`, { params: { suggestion } }),
  generate: (tenantId, params) => api.post(`/ai/dj/tenants/${tenantId}/generate`, null, { params }),
  submitFeedback: (itemId, reason) => api.post(`/ai/dj/feedback/${itemId}`, null, { params: { reason } }),
  getFeedback: (tenantId) => api.get(`/ai/dj/tenants/${tenantId}/feedback`),
};

export const runtimeAPI = {
  resolve: (storeId, tenantId) => api.get(`/runtime/stores/${storeId}/resolve`, { params: { tenant_id: tenantId } }),
};

export default api;
